import { Grid } from '@mui/material';
import { FormikProps } from 'formik';
import { useCallback, useMemo } from 'react';

import { IAddress } from '@modules/fedex/Addresses/interfaces';
import { FormPart } from '@modules/shared/components/FormPart';
import { getOptions } from '@shared/commonFunctions';
import { statesList } from '@shared/constants/statesList';
import { CheckboxField } from '@shared/fields/CheckboxField';
import { PhoneInputField } from '@shared/fields/PhoneInputField';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { ZipCodeInputField } from '@shared/fields/ZipCodeInputField';
import { CancelButton, SaveButton } from '@shared/inputs/Buttons';
import { IOption } from '@shared/interfaces';
import { CommonButtonWrapper, PageContent } from '@styles/CommonStyles';

import { IReadyToShip } from '../interfaces';

interface IReadyToShipProps extends Partial<FormikProps<IReadyToShip>> {
  handleClose: () => void;
  shipmentServiceTypes: IOption[];
  shippingAddresses: IAddress[];
  loading: boolean;
}

export const ReadyToShipForm = ({
  setFieldValue,
  handleClose,
  loading,
  shipmentServiceTypes,
  shippingAddresses,
}: IReadyToShipProps) => {
  const setValues = useCallback((value: string) => {
    const shippingAddress = shippingAddresses.filter((item) => {
      return item.id === value;
    });

    if (shippingAddress && shippingAddress.length) {
      for (const key in shippingAddress[0]) {
        setFieldValue?.(key, shippingAddress[0][key as keyof IAddress]);
      }
      setFieldValue?.('zip', shippingAddress[0].zip.code as keyof IAddress);
    }
  }, [shippingAddresses]);

  const formConfig = useMemo(() => ([
    {
      width: '100%',
      name: 'shipmentAddressProfileId',
      label: 'Select existing nick name or enter a new one below',
      options: getOptions(shippingAddresses),
      onChange: setValues,
      component: SelectAutocompleteField,
    },
    {
      width: '50%',
      name: 'name',
      label: 'Name',
    },
    {
      width: '50%',
      name: 'nickName',
      label: 'Nickname',
    },
    {
      width: '50%',
      name: 'company',
      label: 'Company',
    },
    {
      width: '50%',
      name: 'address',
      label: 'Address',
    },
    {
      width: '50%',
      name: 'address2',
      label: 'Address 2',
    },
    {
      width: '50%',
      name: 'city',
      label: 'City',
    },
    {
      width: '50%',
      name: 'state',
      label: 'State',
      options: statesList,
      component: SelectAutocompleteField,
    },
    {
      width: '50%',
      name: 'zip',
      label: 'Zip',
      component: ZipCodeInputField
    },
    {
      width: '50%',
      name: 'phoneNumber',
      label: 'Phone Number',
      component: PhoneInputField,
    },
    {
      width: '50%',
      name: 'isReturnLabelChecked',
      label: 'Return Label',
      component: CheckboxField,
    },
    {
      width: '100%',
      name: 'serviceType',
      label: 'Type',
      options: shipmentServiceTypes,
      component: SelectAutocompleteField,
    },
  ]
  ), [shipmentServiceTypes, statesList, shippingAddresses]);

  return (
    <>
      <PageContent>
        <Grid container spacing={12}>
          <Grid item md={12}>
            <FormPart config={formConfig} />
          </Grid>
        </Grid>
      </PageContent>

      <CommonButtonWrapper>
        <CancelButton onClick={handleClose} />
        <SaveButton isLoading={loading} />
      </CommonButtonWrapper>
    </>
  );
};
